/**
 * Redact Page (VE-25621): a click on a page with the tool active has the MuPDF
 * render worker scan that page into one box per element (`pageElements.ts`,
 * run in the worker), and adds them as REDACT_PAGE_ELEMENTS — one undo step,
 * deduped against existing manual boxes in the reducer. A click that adds
 * nothing (blank or already fully redacted page) says so instead of silently
 * doing nothing.
 *
 * The result is dropped if the editor moved on to another document while the
 * scan ran, so a slow scan can never land boxes on the wrong file.
 */
import { call, put, select, takeEvery } from 'typed-redux-saga/macro';
import { v4 as uuid } from 'uuid';
import { enqueueSnackbar } from '@common-modules/snackbar';
import { sagaIntl } from '@i18n';
import { scanMupdfPageElements } from '@redact-components/DocumentEditorPage/mupdfRenderWorkerClient';
import {
  REDACT_PAGE_ELEMENTS,
  REDACT_PAGE_ELEMENTS_FAILURE,
  REDACT_PAGE_ELEMENTS_REQUEST,
} from '../actions';
import { pageElementRedactions } from '../pageElements';
import { selectSession } from '../selectors';

export function* redactPageElements({
  payload: { page },
}: ReturnType<typeof REDACT_PAGE_ELEMENTS_REQUEST>) {
  const session = yield* select(selectSession);
  if (!session) {
    return;
  }
  const { tdoId, pdfUrl } = session;
  try {
    const boxes = yield* call(scanMupdfPageElements, pdfUrl, page - 1);
    const current = yield* select(selectSession);
    if (current?.tdoId !== tdoId) {
      return;
    }
    const pageRedactionId = uuid();
    const redactions = pageElementRedactions(
      page,
      boxes,
      pageRedactionId,
      uuid
    );
    yield* put(REDACT_PAGE_ELEMENTS({ page, redactions }));
    const after = yield* select(selectSession);
    const added =
      after?.redactions.some((r) => r.pageRedactionId === pageRedactionId) ??
      false;
    // A page removed or deleted while it was scanned dropped the result on
    // purpose; "nothing left to redact" would misdescribe it.
    const masked =
      !!after?.pageRedacts.includes(page) ||
      !!after?.excludedPages.includes(page);
    if (!added && !masked) {
      yield* put(
        enqueueSnackbar({
          message: sagaIntl().formatMessage(
            {
              id: 'documentRedactPageNothingToRedact',
              defaultMessage: 'Nothing left to redact on page {page}.',
            },
            { page }
          ),
          variant: 'info',
        })
      );
    }
  } catch (err) {
    console.error('[redactPageElements] page scan failed:', page, err);
    yield* put(
      REDACT_PAGE_ELEMENTS_FAILURE({
        page,
        error: err instanceof Error ? err.message : String(err),
      })
    );
    yield* put(
      enqueueSnackbar({
        message: sagaIntl().formatMessage(
          {
            id: 'documentRedactPageFailed',
            defaultMessage: "Couldn't redact page {page}. Please try again.",
          },
          { page }
        ),
        variant: 'error',
      })
    );
  }
}

export function* redactPageElementsSaga() {
  yield* takeEvery(REDACT_PAGE_ELEMENTS_REQUEST, redactPageElements);
}
